import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { fmt, fmtDate } from '../../lib/constants';
import StatCard from '../../components/ui/StatCard';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [recent,  setRecent]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const now        = new Date();
    const thisMonth  = now.toISOString().slice(0, 7);
    const thisYear   = now.getFullYear();
    const lastMonth  = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 7);

    const [
      { count: totalMembers    },
      { count: pendingCount    },
      { count: fullMembers     },
      { count: associateMembers},
      { count: baptisedChildren},
      { count: deceased        },
      { count: newThisMonth    },
      { count: pendingApproval },
      { data:  monthContribs   },
      { data:  lastMonthContribs},
      { data:  yearContribs    },
      { data:  byDistData      },
      { data:  lastSvc         },
      { data:  recentContribs  },
      { data:  upcomingEvents  },
      { data:  pettyData       },
    ] = await Promise.all([
      supabase.from('members').select('*',{count:'exact',head:true}).eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('status','pending'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('membership_type','Full Member').eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('membership_type','Associate Member').eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('membership_type','Baptised Child').eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('status','deceased'),
      supabase.from('members').select('*',{count:'exact',head:true}).gte('joined_date',`${thisMonth}-01`),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('status','pending'),
      supabase.from('contributions').select('amount,type').gte('date',`${thisMonth}-01`),
      supabase.from('contributions').select('amount').gte('date',`${lastMonth}-01`).lte('date',`${lastMonth}-31`),
      supabase.from('contributions').select('amount,type').gte('date',`${thisYear}-01-01`),
      supabase.from('members').select('districts(name)').eq('status','active'),
      supabase.from('sunday_services').select('*').order('date',{ascending:false}).limit(1),
      supabase.from('contributions').select('*,members(first_name,last_name,districts(name))').order('created_at',{ascending:false}).limit(5),
      supabase.from('group_events').select('*,groups(name)').gte('date',now.toISOString().split('T')[0]).order('date',{ascending:true}).limit(5),
      supabase.from('petty_cash').select('balance').order('created_at',{ascending:false}).limit(1)
    ]);

    const sumAmt  = (arr) => (arr||[]).reduce((s,c)=>s+Number(c.amount),0);
    const sumType = (arr,t)=> (arr||[]).filter(c=>c.type===t).reduce((s,c)=>s+Number(c.amount),0);

    const monthTotal    = sumAmt(monthContribs);
    const lastMonthTotal= sumAmt(lastMonthContribs);
    const yearTotal     = sumAmt(yearContribs);
    const monthTithe    = sumType(monthContribs,'Tithe');
    const monthOffering = sumType(monthContribs,'Offering');
    const monthChange   = lastMonthTotal > 0 ? Math.round(((monthTotal - lastMonthTotal)/lastMonthTotal)*100) : 0;

    // District breakdown
    const distMap = {};
    (byDistData||[]).forEach(m => {
      const d = m.districts?.name || 'Unknown';
      distMap[d] = (distMap[d]||0) + 1;
    });

    // Last service stats
    const lastService = lastSvc?.[0];
    let communionCount = 0, attendanceCount = 0;
    if (lastService) {
      const [{count:ac},{count:cc}] = await Promise.all([
        supabase.from('attendance').select('*',{count:'exact',head:true}).eq('service_id',lastService.id).eq('type','service'),
        supabase.from('attendance').select('*',{count:'exact',head:true}).eq('service_id',lastService.id).eq('type','holy_communion'),
      ]);
      communionCount  = cc || 0;
      attendanceCount = ac || 0;
    }

    const pettyBalance = pettyData?.[0]?.balance || 0;

    setStats({
      totalMembers, pendingCount, fullMembers, associateMembers,
      baptisedChildren, deceased, newThisMonth, pendingApproval,
      monthTotal, lastMonthTotal, monthChange, yearTotal,
      monthTithe, monthOffering,
      distMap, lastService, communionCount, attendanceCount,
      pettyBalance,
    });
    setRecent(recentContribs || []);
    setLoading(false);
  }

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:300}}>
      <div style={{textAlign:'center',color:'var(--text3)'}}>
        <img src="/pcea-logo.png" alt="PCEA" style={{width:56,opacity:.4,marginBottom:12}}/>
        <div>Loading dashboard…</div>
      </div>
    </div>
  );

  const maxDist = Math.max(...Object.values(stats.distMap), 1);
  const today   = new Date();
  const greet   = today.getHours() < 12 ? 'Good morning' : today.getHours() < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div>
      {/* ── Welcome banner ── */}
      <div style={{
        background:'linear-gradient(135deg,var(--blue-dark) 0%,var(--blue) 60%,var(--red-dark) 100%)',
        borderRadius:14, padding:'20px 24px', color:'white', marginBottom:20,
        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16,
        position:'relative', overflow:'hidden',
      }}>
        <div style={{position:'absolute',right:20,top:'50%',transform:'translateY(-50%)',opacity:.07,pointerEvents:'none'}}>
          <img src="/pcea-logo.png" alt="" style={{width:100}}/>
        </div>
        <div>
          <div style={{fontSize:13,opacity:.75,marginBottom:4}}>{greet} —</div>
          <div style={{fontSize:22,fontWeight:900,lineHeight:1.2}}>PCEA Thingati Church</div>
          <div style={{fontSize:13,opacity:.75,marginTop:4}}>
            {today.toLocaleDateString('en-KE',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
          </div>
        </div>
        <div style={{display:'flex',gap:20,flexWrap:'wrap'}}>
          {[
            ['Active Members',    stats.totalMembers,  '👥'],
            ['New This Month',    stats.newThisMonth,  '🆕'],
            ['YTD Income',        fmt(stats.yearTotal),'💰'],
          ].map(([l,v,ic])=>(
            <div key={l} style={{textAlign:'center',background:'rgba(255,255,255,.12)',borderRadius:10,padding:'10px 18px'}}>
              <div style={{fontSize:10,opacity:.7,textTransform:'uppercase',letterSpacing:.5,marginBottom:4}}>{l}</div>
              <div style={{fontSize:22,fontWeight:900}}>{ic} {v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pending approval alert ── */}
      {stats.pendingCount > 0 && (
        <div className="alert alert-warn mb-4 no-print" style={{cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between'}} onClick={()=>navigate('/admin/members/pending')}>
          <span>⏳ <strong>{stats.pendingCount} member{stats.pendingCount>1?'s':''}</strong> awaiting approval</span>
          <button className="btn btn-sm" style={{background:'#E65100',color:'white'}}>Review →</button>
        </div>
      )}

      {/* ── Top stat cards ── */}
      <div className="grid-4 mb-4">
        <StatCard icon="💰" label="This Month Income" color="green"
          value={fmt(stats.monthTotal)}
          sub={stats.monthChange!==0?`${stats.monthChange>0?'▲':'▼'} ${Math.abs(stats.monthChange)}% vs last month`:'—'}
        />
        <StatCard icon="🙏" label="Tithes This Month"   value={fmt(stats.monthTithe)}   color="blue" />
        <StatCard icon="🎁" label="Offerings This Month" value={fmt(stats.monthOffering)} color="blue" />
        <StatCard icon="💵" label="Petty Cash Balance"   value={fmt(stats.pettyBalance)}  color={stats.pettyBalance<2000?'red':'green'} />
      </div>

      {/* ── Two column section ── */}
      <div className="grid-2 mb-4">

        {/* Membership breakdown */}
        <div className="card">
          <div className="card-header">
            <h3>Membership Overview</h3>
            <button className="btn btn-outline btn-sm" onClick={()=>navigate('/admin/members')}>View All →</button>
          </div>
          <div style={{padding:'14px 18px'}}>
            {[
              ['Full Members',       stats.fullMembers,       'var(--blue-dark)',   'badge-blue'],
              ['Associate Members',  stats.associateMembers,  'var(--blue)',        'badge-blue'],
              ['Baptised Children',  stats.baptisedChildren,  'var(--success)',     'badge-green'],
              ['Pending Approval',   stats.pendingApproval,   '#E65100',           'badge-amber'],
              ['Deceased (recorded)',stats.deceased,           'var(--text3)',      'badge-gray'],
            ].map(([l,v,c,badge])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                <span style={{fontSize:13,color:'var(--text2)'}}>{l}</span>
                <span style={{fontWeight:800,fontSize:15,color:c}}>{v}</span>
              </div>
            ))}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',fontWeight:800}}>
              <span style={{fontSize:14}}>TOTAL ACTIVE</span>
              <span style={{fontSize:20,color:'var(--blue-dark)'}}>{stats.totalMembers}</span>
            </div>
          </div>
        </div>

        {/* Financial summary */}
        <div className="card">
          <div className="card-header">
            <h3> Financial Summary</h3>
            <button className="btn btn-outline btn-sm" onClick={()=>navigate('/admin/financials')}>View →</button>
          </div>
          <div style={{padding:'14px 18px'}}>
            {/* YTD bar */}
            <div style={{marginBottom:16}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text3)',marginBottom:4,fontWeight:700,textTransform:'uppercase',letterSpacing:.4}}>
                <span>Year to Date — {new Date().getFullYear()}</span>
                <span>{fmt(stats.yearTotal)}</span>
              </div>
              <div style={{height:8,background:'var(--bg)',borderRadius:4,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${Math.min((stats.monthTotal/Math.max(stats.yearTotal,1))*100*12,100)}%`,background:'linear-gradient(to right,var(--blue),var(--red))',borderRadius:4}}/>
              </div>
            </div>
            {[
              ['This Month Total',    fmt(stats.monthTotal),     stats.monthChange!==0?`${stats.monthChange>0?'+':''}${stats.monthChange}% vs last month`:null],
              ['Last Month Total',    fmt(stats.lastMonthTotal), null],
              ['Year Total (YTD)',    fmt(stats.yearTotal),      null],
              ['Month Tithes',        fmt(stats.monthTithe),     null],
              ['Month Offerings',     fmt(stats.monthOffering),  null],
            ].map(([l,v,sub])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:'1px solid var(--border)'}}>
                <div>
                  <div style={{fontSize:13,color:'var(--text2)'}}>{l}</div>
                  {sub&&<div style={{fontSize:10,color:stats.monthChange>=0?'var(--success)':'var(--red)',fontWeight:700}}>{sub}</div>}
                </div>
                <span style={{fontWeight:700,color:'var(--blue-dark)',fontSize:14}}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Second row ── */}
      <div className="grid-2 mb-4">

        {/* Members by district chart */}
        <div className="card">
          <div className="card-header"><h3>Members by District</h3></div>
          <div style={{padding:'14px 18px'}}>
            <div className="bar-chart">
              {Object.entries(stats.distMap).sort((a,b)=>b[1]-a[1]).map(([d,c])=>(
                <div key={d} className="bar-row">
                  <span className="bar-label">{d}</span>
                  <div className="bar-track">
                    <div className="bar-fill blue" style={{width:`${Math.round((c/maxDist)*100)}%`}}>
                      <span className="bar-fill-val">{c}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Last service + recent contributions */}
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* Last Sunday service */}
          <div className="card">
            <div className="card-header">
              <h3>Last Sunday Service</h3>
              <button className="btn btn-outline btn-sm" onClick={()=>navigate('/admin/attendance')}>Attendance →</button>
            </div>
            <div style={{padding:'14px 18px'}}>
              {stats.lastService ? (
                <div>
                  <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>
                    {fmtDate(stats.lastService.date)}
                    {stats.lastService.preacher && ` · ${stats.lastService.preacher}`}
                  </div>
                  {stats.lastService.sermon_title && (
                    <div style={{fontWeight:700,fontSize:14,marginBottom:10,color:'var(--blue-dark)'}}>
                      "{stats.lastService.sermon_title}"
                    </div>
                  )}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                    {[
                      ['👥','Attendance',stats.attendanceCount],
                      ['🍞','Communion', stats.communionCount],
                      ['💰','Offering',  fmt(stats.lastService.total_offering||0)],
                    ].map(([ic,l,v])=>(
                      <div key={l} style={{background:'var(--bg)',borderRadius:8,padding:'8px 10px',textAlign:'center'}}>
                        <div style={{fontSize:16}}>{ic}</div>
                        <div style={{fontSize:18,fontWeight:800,color:'var(--blue-dark)'}}>{v}</div>
                        <div style={{fontSize:10,color:'var(--text3)',fontWeight:700,textTransform:'uppercase',letterSpacing:.4}}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{color:'var(--text3)',fontSize:13}}>No services recorded yet.</div>
              )}
            </div>
          </div>

          {/* Quick actions */}
          <div className="card">
            <div className="card-header"><h3> Quick Actions</h3></div>
            <div style={{padding:'14px 18px',display:'flex',flexDirection:'column',gap:8}}>
              {[
                ['Add Member',            '/admin/members/add'],
                ['Mark Attendance',       '/admin/attendance'],
                ['Record Contribution',   '/admin/financials/record'],
                ['View Calendar',         '/admin/calendar'],
                ['Life Events',           '/admin/tracking'],
                ['Inventory',             '/admin/inventory'],
              ].map(([l,p])=>(
                <button key={p} className="btn btn-outline btn-sm"
                  style={{justifyContent:'flex-start',textAlign:'left'}}
                  onClick={()=>navigate(p)}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Recent contributions ── */}
      <div className="card mb-4">
        <div className="card-header">
          <h3>Recent Contributions</h3>
          <button className="btn btn-outline btn-sm" onClick={()=>navigate('/admin/financials')}>View All →</button>
        </div>
        {recent.length === 0 ? (
          <div style={{textAlign:'center',padding:24,color:'var(--text3)',fontSize:13}}>No contributions recorded yet.</div>
        ) : (
          <div>
            {recent.map(c=>(
              <div key={c.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 18px',borderBottom:'1px solid var(--border)'}}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:32,height:32,borderRadius:'50%',background:'var(--blue-light)',color:'var(--blue-dark)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>
                    {((c.members?.first_name||'?')[0]+( c.members?.last_name||'?')[0]).toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontWeight:700,fontSize:13}}>{c.members?`${c.members.first_name} ${c.members.last_name}`:'—'}</div>
                    <div style={{fontSize:11,color:'var(--text3)'}}>{fmtDate(c.date)} · {c.type} · {c.members?.districts?.name||'—'}</div>
                  </div>
                </div>
                <div style={{fontWeight:800,color:'var(--blue-dark)',fontSize:15}}>{fmt(c.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Church info panel ── */}
      <div className="grid-2">
        {/* Church facts */}
        <div className="card">
          <div className="card-header"><h3>Church Information</h3></div>
          <div style={{padding:'14px 18px'}}>
            {[
              ['Church Name',     'PCEA Thingati Parish'],
              ['Denomination',    'Presbyterian Church of East Africa'],
              ['Location',        'Thingati, Ngecha, Limuru'],
              ['Motto',           'Faith · Love · Hope'],
              ['Active Members',  stats.totalMembers],
              ['Full Members',    stats.fullMembers],
              ['Groups / Ministries', '12'],
            ].map(([l,v])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'7px 0',borderBottom:'1px solid var(--border)',fontSize:13}}>
                <span style={{color:'var(--text3)',fontWeight:600}}>{l}</span>
                <span style={{fontWeight:700,textAlign:'right',maxWidth:'60%'}}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* System summary */}
        <div className="card">
          <div className="card-header"><h3>System Summary</h3></div>
          <div style={{padding:'14px 18px'}}>
            {[
              ['Active Members',         stats.totalMembers],
              ['Pending Approvals',      stats.pendingCount],
              ['This Month Income',      fmt(stats.monthTotal)],
              ['Year to Date Income',    fmt(stats.yearTotal)],
              ['Last Service Attendance',stats.attendanceCount],
              ['Last Communion Count',   stats.communionCount],
              ['Petty Cash Balance',     fmt(stats.pettyBalance)],
            ].map(([l,v])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'7px 0',borderBottom:'1px solid var(--border)',fontSize:13}}>
                <span style={{color:'var(--text3)'}}>{l}</span>
                <span style={{fontWeight:700,color:'var(--blue-dark)'}}>{v}</span>
              </div>
            ))}
            <div style={{marginTop:12,padding:'10px 14px',background:'var(--blue-light)',borderRadius:8,fontSize:11,color:'var(--blue-dark)',textAlign:'center',fontWeight:600}}>
              PCEA Thingati · Ngecha Parish · {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}